import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import ArrowRightIcon from '@frontend/static/icons/arrow-right.svg';
import { space, brandText, brandAlt, neutral, textSans, headline, from, until, } from '@guardian/source-foundations';
import { getLastOneOffContributionDate, MODULES_VERSION, shouldHideSupportMessaging, } from '@root/src/web/lib/contributions';
import { setAutomat } from '@root/src/web/lib/setAutomat';
import { getCookie, } from '@guardian/libs';
import { useHasBeenSeen } from '@root/src/web/lib/useHasBeenSeen';
import { addTrackingCodesToUrl } from '@root/src/web/lib/acquisitions';
import { sendOphanComponentEvent, submitComponentEvent, } from '@root/src/web/browser/ophan/ophan';
import { useOnce } from '@root/src/web/lib/useOnce';
const headerStyles = css `
	padding-top: 39px;
	padding-left: 10px;
	max-width: 310px;

	${from.mobileMedium} {
		padding-top: 44px;
	}

	${from.mobileLandscape} {
		padding-left: ${space[5]}px;
	}

	${from.tablet} {
		padding-top: ${space[1]}px;
		padding-bottom: ${space[5]}px;
		padding-left: ${space[5]}px;
		max-width: 340px;
	}

	${from.desktop} {
		max-width: none;
	}
`;
const messageStyles = (isThankYouMessage) => css `
	color: ${brandAlt[400]};
	${headline.xxsmall({ fontWeight: 'bold' })};
	padding-top: 3px;
	margin-bottom: 3px;

	${from.desktop} {
		${headline.xsmall({ fontWeight: 'bold' })}
	}

	${from.leftCol} {
		${isThankYouMessage
    ? headline.small({ fontWeight: 'bold' })
    : headline.medium({ fontWeight: 'bold' })}
	}
`;
const linkStyles = css `
	background: ${brandAlt[400]};
	border-radius: 16px;
	box-sizing: border-box;
	color: ${neutral[7]};
	float: left;
	${textSans.small()};
	font-weight: 700;
	height: 32px;
	text-decoration: none;
	padding: 6px 12px 0 12px;
	line-height: 18px;
	position: relative;
	margin-right: 10px;
	margin-bottom: 6px;

	${from.mobileMedium} {
		padding-right: 34px;
	}

	svg {
		fill: currentColor;
		position: absolute;
		right: 3px;
		top: 50%;
		height: 32px;
		width: 32px;
		transform: translate(0, -50%);
		transition: transform 0.3s ease-in-out;

		${until.mobileMedium} {
			display: none;
		}
	}

	:hover svg {
		transform: translate(3px, -50%);
	}
`;
const hidden = css `
	display: none;
`;
const hiddenUntilTablet = css `
	${until.tablet} {
		display: none;
	}
`;
const hiddenFromTablet = css `
	${from.tablet} {
		display: none;
	}
`;
const subMessageStyles = css `
	color: ${brandText.primary};
	${textSans.medium()};
	margin: 5px 0;
`;
const ReaderRevenueLinksRemote = ({ edition, countryCode, pageViewId, contributionsServiceUrl, ophanRecord, }) => {
    const [supportHeaderResponse, setSupportHeaderResponse] = useState(null);
    const [SupportHeader, setSupportHeader] = useState(null);
    useOnce(() => {
        setAutomat();
        const requestData = {
            tracking: {
                ophanPageId: pageViewId,
                platformId: 'GUARDIAN_WEB',
                referrerUrl: window.location.origin + window.location.pathname,
                clientName: 'dcr',
            },
            targeting: {
                showSupportMessaging: !shouldHideSupportMessaging(),
                edition,
                countryCode,
                modulesVersion: MODULES_VERSION,
                mvtId: Number(getCookie({ name: 'GU_mvt_id', shouldMemoize: true })),
                lastOneOffContributionDate: getLastOneOffContributionDate(),
            },
        };
        fetch(`${contributionsServiceUrl}/header`, {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestData),
        })
            .then((result) => result.json())
            .then((response) => {
            if (!response.data) {
                return null;
            }
            setSupportHeaderResponse(response.data);
            const { module } = response.data;
            return window
                .guardianPolyfilledImport(module.url)
                .then((headerModule) => {
                setSupportHeader(() => headerModule.Header);
            });
        })
            .catch((error) => {
            const msg = `Error importing RR header links: ${error}`;
            // eslint-disable-next-line no-console
            console.log(msg);
            window.guardian.modules.sentry.reportError(new Error(msg), 'rr-header-links');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryCode]);
    if (SupportHeader && supportHeaderResponse) {
        return (_jsx("div", Object.assign({ css: headerStyles }, { children: _jsx(SupportHeader, Object.assign({ submitComponentEvent: (componentEvent) => submitComponentEvent(componentEvent, ophanRecord) }, supportHeaderResponse.module.props), void 0) }), void 0));
    }
    return null;
};
export const ReaderRevenueLinksNative = ({ edition, dataLinkNamePrefix, inHeader, urls, ophanRecord, pageViewId, }) => {
    const hideSupportMessaging = shouldHideSupportMessaging();
    // Only the header component is in the AB test
    const testName = inHeader ? 'RRHeaderLinks' : 'RRFooterLinks';
    const campaignCode = `${testName}_control`;
    const tracking = {
        abTestName: testName,
        abTestVariant: 'control',
        componentType: inHeader ? 'ACQUISITIONS_HEADER' : 'ACQUISITIONS_FOOTER',
        campaignCode,
    };
    const [hasBeenSeen, setNode] = useHasBeenSeen({
        threshold: 0,
        debounce: true,
    });
    useEffect(() => {
        if (!hideSupportMessaging && inHeader) {
            sendOphanComponentEvent('INSERT', tracking, ophanRecord);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (hasBeenSeen && inHeader) {
            sendOphanComponentEvent('VIEW', tracking, ophanRecord);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasBeenSeen]);
    const getUrl = (rrType) => {
        if (inHeader) {
            return addTrackingCodesToUrl({
                base: `https://support.theguardian.com/${rrType}`,
                componentType: 'ACQUISITIONS_HEADER',
                componentId: campaignCode,
                campaignCode,
                abTest: {
                    name: testName,
                    variant: 'control',
                },
                pageViewId,
                referrerUrl: window.location.origin + window.location.pathname,
            });
        }
        return urls[rrType];
    };
    if (hideSupportMessaging) {
        return (_jsx("div", Object.assign({ css: inHeader && headerStyles }, { children: _jsxs("div", Object.assign({ css: inHeader && hiddenUntilTablet }, { children: [_jsx("div", Object.assign({ css: messageStyles(true) }, { children: " Thank you " }), void 0), _jsx("div", Object.assign({ css: subMessageStyles }, { children: "Your support powers our independent journalism" }), void 0)] }), void 0) }), void 0));
    }
    const ContributeButton = () => (_jsxs("a", Object.assign({ css: linkStyles, href: getUrl('contribute'), "data-link-name": `${dataLinkNamePrefix}contribute-cta` }, { children: ["Contribute ", _jsx(ArrowRightIcon, {}, void 0)] }), void 0));
    const SubscribeButton = () => (_jsxs("a", Object.assign({ css: linkStyles, href: getUrl('subscribe'), "data-link-name": `${dataLinkNamePrefix}subscribe-cta` }, { children: ["Subscribe ", _jsx(ArrowRightIcon, {}, void 0)] }), void 0));
    const PrimaryButton = edition === 'UK' ? SubscribeButton : ContributeButton;
    const SecondaryButton = edition === 'UK' ? ContributeButton : SubscribeButton;
    return (_jsxs("div", Object.assign({ ref: setNode, css: inHeader && headerStyles }, { children: [_jsxs("div", Object.assign({ css: inHeader && hiddenUntilTablet }, { children: [_jsx("div", Object.assign({ css: messageStyles(false) }, { children: _jsx("span", { children: "Support the\u00A0Guardian" }, void 0) }), void 0), _jsx("div", Object.assign({ css: subMessageStyles }, { children: _jsx("div", { children: "Available for everyone, funded by readers" }, void 0) }), void 0), _jsx(PrimaryButton, {}, void 0), _jsx(SecondaryButton, {}, void 0)] }), void 0), _jsx("div", Object.assign({ css: inHeader ? hiddenFromTablet : hidden }, { children: _jsx(PrimaryButton, {}, void 0) }), void 0)] }), void 0));
};
export const ReaderRevenueLinks = ({ edition, countryCode, dataLinkNamePrefix, inHeader, remoteHeaderEnabled, urls, contributionsServiceUrl, ophanRecord, pageViewId = '', }) => {
    if (inHeader && remoteHeaderEnabled) {
        return (_jsx(ReaderRevenueLinksRemote, { edition: edition, countryCode: countryCode, pageViewId: pageViewId, contributionsServiceUrl: contributionsServiceUrl, ophanRecord: ophanRecord }, void 0));
    }
    return (_jsx(ReaderRevenueLinksNative, { edition: edition, countryCode: countryCode, dataLinkNamePrefix: dataLinkNamePrefix, inHeader: inHeader, remoteHeaderEnabled: remoteHeaderEnabled, urls: urls, contributionsServiceUrl: contributionsServiceUrl, ophanRecord: ophanRecord, pageViewId: pageViewId }, void 0));
};
